import React, { useContext, useEffect, useState } from 'react';
import { Box, Divider, Typography } from '@mui/material';

// Components
import RoleDescriptionList from './RoleDescriptionList';
import SmartForm from '@component/smartForm/SmartForm';
import SmartTextInput from '@component/smartForm/SmartInputText';
import SmartImageUpload from '@component/smartForm/SmartImageUpload';
import SmartRadioInput from '@component/smartForm/SmartRadioInput';

// Contexts & Hooks
import { AccountContext } from "@context/AccountProvider";
import { AlertContext } from "@context/AlertProvider";
import { useTranslation } from "react-i18next";

// Queries & Mutations
import { useAdminAccountUUIDQuery } from '@query/accountQuery';
import { useAdminAccountUpdateMutation } from '@mutation/accountMutation';
import { useAllAdminRolesQuery } from '@query/roleQuery';

// Services, Utils & Validators
import { mapRole, Roles } from '@service/roleService';
import { removeEmptyValues, pickFromObject } from '@utils/generalUtils';
import { notEmptyValidator } from '@component/smartForm/fieldValidators';

function isSiteAdminOrEmployeePicked(fetchedRoles, pickedRoleUuid) {
    for (const role of fetchedRoles || []) {
        const mappedRole = mapRole(role.apiName);
        if (role.value === pickedRoleUuid &&
            (mappedRole.level === Roles.SITE_ADMIN.level || mappedRole.level === Roles.SITE_EMPLOYEE.level)) {
            return true;
        }
    }
    return false;
}

// 👈 Added onCancel prop for better integration with SidePanel
function AccountForm({ uuid, onCancel }) {
    const [fetchedRoles, setFetchedRoles] = useState([]);
    const [showSiteSelection, setShowSiteSelection] = useState(false);
    const [formModel, setFormModel] = useState(null);

    const { sites, club } = useContext(AccountContext);
    const { addAlertBanner } = useContext(AlertContext);
    const { t } = useTranslation(["account", "main", "role"]);

    const adminAccountUUIDQuery = useAdminAccountUUIDQuery(uuid, { enabled: !!uuid });
    const allRolesQuery = useAllAdminRolesQuery();
    const adminAccountUpdateMutation = useAdminAccountUpdateMutation();

    useEffect(() => {
        if (adminAccountUUIDQuery.data?.data) {
            setFormModel(adminAccountUUIDQuery.data.data);
        }
    }, [adminAccountUUIDQuery.data]);

    useEffect(() => {
        if (allRolesQuery.data?.data) {
            const fetchedRolesClean = allRolesQuery.data.data.map((roleRecord) => ({
                value: roleRecord.uuid,
                label: t(mapRole(roleRecord.name).translationKey),
                apiName: roleRecord.name
            }));
            setFetchedRoles(fetchedRolesClean);
        }
    }, [allRolesQuery.data, t]);

    const handleSave = (newModel) => {
        const cleanModel = removeEmptyValues(pickFromObject(newModel, ["site_uuid", "club_uuid", "role_uuid"]));
        if (!cleanModel?.club_uuid && !cleanModel?.site_uuid) {
            cleanModel.club_uuid = club?.uuid;
        }
        adminAccountUpdateMutation.mutate({ data: {...cleanModel, account_uuid: uuid} }, {
            onSuccess: () => {
                addAlertBanner(t("main:saveSuccess"), "success");
                adminAccountUUIDQuery.refetch();
            },
            onError: (response) => {
                if (response.status === 403) addAlertBanner(t("main:noPermissionLong"), 'error');
                else if (response.status === 409) addAlertBanner(t("account:accountCreateConfilict"), 'error');
                else addAlertBanner(t("main:saveError"), "error");
                adminAccountUUIDQuery.refetch();
            }
        });
    };

    useEffect(() => {
        if (!formModel || !fetchedRoles.length) return;

        const needsSiteSelection = isSiteAdminOrEmployeePicked(fetchedRoles, formModel.role_uuid);
        if (needsSiteSelection) {
            setShowSiteSelection(true);
            // If role changes to site-specific, ensure club_uuid is unset in the model
            if (formModel.club_uuid) {
                setFormModel(prev => ({ ...prev, club_uuid: "" }));
            }
        } else {
            setShowSiteSelection(false);
            // If role changes to club-specific, ensure site_uuid is unset
            if (formModel.site_uuid) {
                 setFormModel(prev => ({ ...prev, site_uuid: "" }));
            }
        }
    }, [formModel, fetchedRoles]);

    // 👈 Correctly determine the loading state from all relevant queries/mutations
    const isLoading = adminAccountUUIDQuery.isFetching || allRolesQuery.isFetching || adminAccountUpdateMutation.isPending;

    return (
        <Box sx={{ p: 2, width: "100%", display:"flex", gap:2.5, flexDirection:"column"}}>
            <SmartForm
                formModel={formModel}
                setFormModel={setFormModel}
                onSave={handleSave}
                onCancel={onCancel}
                isLoading={isLoading}
                contentSx={{ display: "flex", flexDirection: "column", gap: 2.5 }} // Vertical layout
                buttonPanelSx={{
                    top: "10px",
                    right: "10px",
                }}
            >
                <SmartImageUpload
                    field="file_image_uuid"
                    editable={false}
                    imageSx={{
                        width: "auto",
                        minHeight: "310px",
                        objectFit: "contain",
                        backgroundColor:"#fff"
                    }}
                />
                <Typography variant="h5" textAlign="center">
                    {formModel?.first_name} {formModel?.last_name}
                </Typography>

                <SmartTextInput field="email" label={t("E-mail")} editable={false} />
                <Divider />
                
                <SmartRadioInput
                    field="role_uuid"
                    label={t("accountRole")}
                    options={fetchedRoles}
                    validator={notEmptyValidator}
                />

                {showSiteSelection && (
                    <SmartRadioInput
                        field="site_uuid"
                        label={t("roleSite")}
                        validator={notEmptyValidator}
                        options={sites.map((site) => ({
                            value: site.uuid,
                            label: site.name
                        }))}
                    />
                )}
            </SmartForm>
            <RoleDescriptionList/>
        </Box>
    );
}

export default AccountForm;